import React from 'react';
import {
    SkeletonBodyText,
    Page,
    PageActions,
    Card,
    Layout,
    Banner,
    TextField,
    RadioButton,
    Select,
    Stack
} from '@shopify/polaris';
import { apiFetch as fetch, CronOptions } from '../../common/Helpers';
import { Helmet } from 'react-helmet';

class CreateAutomationPage extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            isLoading: true,
            isCreating: false,
            automation: {
                automationType: "cron",
                automationValue: "EveryDay",
                name: null,
                operationId: null
            },
            nameError: this.getNameError(null),
            operationError: this.getOperationError(null),
            operationHasExistingAutomation: false,
            operations: []
        };

        const urlParams = new URLSearchParams(window.location.search);
        const operationId = urlParams.get('operationId');

        if (operationId != null) {
            this.state.automation.operationId = operationId;
            this.state.operationError = this.getOperationError(operationId);
        }
    }

    render() {

        const nameChanged = this.state.automationOriginalName !== this.state.automation.name;
        const automationTypeChanged = this.state.automationOriginalAutomationType !== this.state.automation.automationType;
        const automationValueChanged = this.state.automationOriginalAutomationValue !== this.state.automation.automationValue;
        const automationOperationIdChanged = this.state.automationOriginalOperationId !== this.state.automation.operationId;
        const hasNameError = this.state.nameError != null;
        const hasOperationsError = this.state.operationError != null;
        const hasChanges = nameChanged || automationTypeChanged || automationValueChanged || automationOperationIdChanged;
        const canSave = hasChanges && !hasNameError && !hasOperationsError && !this.state.operationHasExistingAutomation;
        var primary = { content: 'Create', onAction: this.handleCreate, disabled: !canSave, loading: this.state.isCreating };

        let options = this.state.operations.map((_) => {
            return { label: _.description, value: _.id }
        });

        options = [
            { label: "--- Choose tag operation ---", value: "" },
            ...options,
        ];

        return (

            <div>
                <Helmet>
                    <title>{"Tag Genie - New Automation"}</title>
                </Helmet>
                {this.state.isLoading && <SkeletonBodyText lines="20" />}
                {!this.state.isLoading && <Page
                    title={"Create Automation"}
                    primaryAction={primary}
                    breadcrumbs={[{ content: 'Automations', url: '/automations' }]}
                >
                    <Layout>
                        <Layout.Section>
                            <Card title="Automation Name" sectioned>
                                <TextField
                                    value={this.state.automation.name}
                                    error={this.state.nameError}
                                    onChange={this.handleNameChange}></TextField>
                            </Card>
                        </Layout.Section>
                        <Layout.Section>
                            <Card title="Operation to automate" sectioned>
                                <Select
                                    options={options}
                                    onChange={this.handleSelectedChanged}
                                    value={this.state.automation.operationId}
                                    error={this.state.operationError}
                                />
                                <br />
                                {this.state.operationHasExistingAutomation && <Banner
                                    title="Already has automation"
                                    status="critical"
                                >
                                    <p>
                                        This operation has another automation associated with it. Only one automation per operation is allowed.
                                    </p>
                                </Banner>
                                }
                            </Card>
                        </Layout.Section>
                        <Layout.Section>
                            <Card title="Automation Type" sectioned>
                                <Stack vertical>
                                    <RadioButton
                                        label="Recurring"
                                        helpText="Operation will be executed on a recurring schedule."
                                        checked={this.state.automation.automationType === 'cron'}
                                        id="automationTypeCron"
                                        name="automationType"
                                        onChange={this.handleCronSelected}
                                    />
                                    <RadioButton
                                        label="Specific Date & Time"
                                        helpText="The operation will be executed one time at the specified date & time."
                                        id="automationTypeDateTime"
                                        name="automationType"
                                        checked={this.state.automation.automationType === 'dateTime'}
                                        onChange={this.handleDateTimeSelected}
                                    />
                                </Stack>
                                <Card.Section>
                                    <Stack>
                                        {this.state.automation.automationType === "cron" && <Select
                                            label="Frequency"
                                            options={CronOptions}
                                            onChange={this.handleFrequencyChange}
                                            value={this.state.automation.automationValue}
                                        />
                                        }

                                        {this.state.automation.automationType === "dateTime" && <TextField
                                            label="Execution time"
                                            type="datetime-local"
                                            onChange={this.handleExecutionTimeChange}
                                            value={this.state.automation.automationValue}
                                        />
                                        }
                                    </Stack>
                                </Card.Section>
                            </Card>
                        </Layout.Section>
                    </Layout>
                    <PageActions
                        primaryAction={primary}
                        secondaryActions={[
                            {
                                content: 'Delete',
                                onAction: this.handleCancel,
                                destructive: true,
                            },
                        ]}
                    />
                </Page>}
            </div>
        );
    }

    handleSelectedChanged = (selected) => {
        var newAutomation = { ...this.state.automation };
        newAutomation.operationId = selected;

        this.setState({
            automation: newAutomation,
            operationError: this.getOperationError(selected)
        });

        this.fetchOperationStatus(selected);
    }

    togglePopoverActive = () => {
        this.setState({ popoverActive: !this.state.popoverActive });
    }

    handleCronSelected = () => {
        var newAutomation = { ...this.state.automation };

        newAutomation.automationType = "cron";
        newAutomation.automationValue = "EveryHour";

        this.setState({ automation: newAutomation });
    }

    handleDateTimeSelected = () => {
        let initDate = new Date();
        initDate.setDate(initDate.getDate() + 1);

        var newAutomation = { ...this.state.automation };

        newAutomation.automationType = "dateTime";
        newAutomation.automationValue = this.formatDate(initDate);

        this.setState({ automation: newAutomation });
    }

    pad(number) {
        var r = String(number);
        if (r.length === 1) {
            r = '0' + r;
        }
        return r;
    }

    formatDate(date) {
        return date.getUTCFullYear() +
            '-' + this.pad(date.getMonth() + 1) +
            '-' + this.pad(date.getDate()) +
            'T' + this.pad(date.getHours()) +
            ':' + this.pad(date.getMinutes());
    };

    handleFrequencyChange = (value) => {
        var newAutomation = { ...this.state.automation };
        newAutomation.automationValue = value;

        this.setState({ automation: newAutomation });
    }

    handleExecutionTimeChange = (value) => {
        var newAutomation = { ...this.state.automation };
        newAutomation.automationValue = value;

        this.setState({ automation: newAutomation });
    }

    handleNameChange = (newValue) => {
        var newAutomation = { ...this.state.automation };
        newAutomation.name = newValue;
        this.setState({
            automation: newAutomation,
            nameError: this.getNameError(newValue)
        });
    }

    getNameError = (name) => {
        return (name === "" || name === null) ? "Name is required" : null;
    }

    getOperationError = (operationId) => {
        return (operationId === "" || operationId === null) ? "Operation must be selected" : null;
    }

    handleCancel = () => {
        this.props.history.push("/automations");
    }

    handleCreate = () => {

        let finalAutomationValue = this.state.automation.automationValue;
        if (this.state.automation.automationType === "dateTime") {
            finalAutomationValue = (new Date(this.state.automation.automationValue)).toISOString();
        }

        this.setState({ isCreating: true });

        fetch(`api/automations`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                name: this.state.automation.name,
                type: this.state.automation.automationType,
                value: finalAutomationValue,
                operationId: this.state.automation.operationId,
            })
        })
            .then(_ => {
                this.props.history.push("/automations");
            });
    }

    fetchOperations = () => {
        fetch(`api/operations/latest`, {
            method: 'GET'
        })
            .then(response => response.json())
            .then(data => this.setState({
                operations: data,
                isLoading: false
            }));
    }

    fetchOperationStatus = (operationId) => {
        fetch(`api/operations/tag/${operationId}/status`, {
            method: 'GET'
        })
            .then(response => response.json())
            .then(data => {

                this.setState({
                    operationHasExistingAutomation: data.hasAutomation,
                });
            });
    }

    componentDidMount() {
        this.fetchOperations();
    }
}

export default CreateAutomationPage;